import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/Cutting';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const CUTTING_API_REDUCER_KEY = 'cuttings';
export const CUTTING_PATH = 'cuttings';

export const cuttingApi = createApi({
    reducerPath: CUTTING_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['Cutting', 'Cuttings'],
    endpoints: (builder) => ({
        getCuttingList: builder.query<PaginationResult<Entity.Cutting>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ CUTTING_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['Cuttings'],
        }),
        getCutting: builder.query<Entity.Cutting, Entity.PrimaryCutting>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = cuttingNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ CUTTING_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['Cutting'],
        }),
        getCuttingMultiple: builder.query<Entity.Cutting, Entity.PrimaryCutting[]>({
            query: (ids) => {
                return ({
                    url: `/${ CUTTING_PATH }/ids${Entity.PrimaryCutting.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['Cuttings'],
        }),
        getCuttingPresentation: builder.query<PaginationResult<Entity.Cutting>, Entity.PresentationCuttingRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ CUTTING_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['Cuttings'],
        }),
        getCuttingAdvancedPresentation: builder.query<PaginationResult<Entity.Cutting>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ CUTTING_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['Cuttings'],
        }),
        
        insertCutting: builder.mutation<Entity.Cutting, Entity.CreateCutting>({
            query: (toInsert) => {
                return ({
                    url: `/${ CUTTING_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    cuttingApi.util.upsertQueryData('getCutting', Entity.PrimaryCutting.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['Cuttings'],
        }),
        insertMultipleCutting: builder.mutation<Entity.Cutting[], Entity.CreateCutting[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ CUTTING_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            cuttingApi.util.upsertQueryData('getCutting', Entity.PrimaryCutting.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['Cuttings'],
        }),
        updateCutting: builder.mutation<Entity.Cutting, Entity.UpdateEntityCutting>({
            query: (toUpdate) => {
                return ({
                    url: `/${ CUTTING_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['Cuttings'],
        }),
        updateMultipleCutting: builder.mutation<Entity.Cutting[], Entity.UpdateEntityCutting[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ CUTTING_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['Cuttings'],
        }),
        deleteCutting: builder.mutation<Entity.Cutting, Entity.Cutting>({
            query: (toDelete) => {
                return ({
                    url: `/${ CUTTING_PATH }/${Entity.PrimaryCutting.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Cuttings'],
        }),
        deleteMultipleCutting: builder.mutation<Entity.Cutting[], Entity.PrimaryCutting[]>({
            query: (ids) => {
                return ({
                    url: `/${ CUTTING_PATH }/batch${Entity.PrimaryCutting.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Cuttings'],
        }),
    }),
})

export const [cuttingNormalizer, cuttingNormalizerMiddleware] = createNormalizationMiddleware(CUTTING_API_REDUCER_KEY, cuttingApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetCuttingListQuery,
    useGetCuttingMultipleQuery,
    useGetCuttingPresentationQuery,
    useGetCuttingQuery,
    useInsertCuttingMutation,
    useInsertMultipleCuttingMutation,
    useUpdateCuttingMutation,
    useUpdateMultipleCuttingMutation,
    useDeleteCuttingMutation,
    useDeleteMultipleCuttingMutation,
    useGetCuttingAdvancedPresentationQuery,
    
} = cuttingApi
